/**
 * 望舒菜单配置
 * v7.0 - 六域自动化智能体菜单架构
 *
 * 重构说明（v7.0）：
 * 1. 九域能力菜单收敛为六个产品入口：智能体工作台/业务工作台/数据平台/智能体工程/自动化运营/管理与运维
 * 2. 面向用户的文案统一使用“智能体”，不再暴露裸 Agent / AI 助手 / 智能助手
 * 3. 模型接入、模型微调进入模型管理菜单，页面可先保持待开发态
 * 4. Phase 1 仅改菜单域和标题，不改页面路由（旧 URL 全部保留）
 * 5. 评审文档：docs/reviews/menu-naming-final-review-2026-05-14.md
 */

import IconApps from '@arco-design/web-vue/es/icon/icon-apps'
import IconBook from '@arco-design/web-vue/es/icon/icon-book'
import IconDashboard from '@arco-design/web-vue/es/icon/icon-dashboard'
import IconRobot from '@arco-design/web-vue/es/icon/icon-robot'
import IconSafe from '@arco-design/web-vue/es/icon/icon-safe'
import IconSettings from '@arco-design/web-vue/es/icon/icon-settings'
import IconStorage from '@arco-design/web-vue/es/icon/icon-storage'
import IconThunderbolt from '@arco-design/web-vue/es/icon/icon-thunderbolt'
import { markRaw, type Component } from 'vue'

/**
 * 菜单项接口定义
 */
export interface MenuItem {
  key: string
  title: string
  icon?: Component
  route?: string
  children?: MenuItem[]
}

// =============================================
// 1. 历史数据目录菜单（兼容导出）
// =============================================

export const ASSET_MENU: MenuItem[] = [
  {
    key: 'asset-catalog',
    title: '数据目录',
    icon: markRaw(IconStorage),
    children: [
      { key: 'datalake-catalog', title: '数据目录', route: '/knowledge/datalake/unified-catalog' },
      { key: 'track-catalog', title: '埋点目录', route: '/knowledge/datalake/track-catalog' },
      { key: 'datalake-datasets', title: '数据集', route: '/knowledge/datalake/datasets' },
      { key: 'governance-tables-metadata', title: '业务元数据', route: '/knowledge/tables-metadata' },
    ]
  },
]

// =============================================
// 2. 历史数据开发菜单（兼容导出）
// =============================================

export const DEVELOPMENT_MENU: MenuItem[] = []

// =============================================
// 3. 数据集成
// =============================================

export const INTEGRATION_MENU: MenuItem[] = []

// =============================================
// 4. 数据治理
// =============================================

export const GOVERNANCE_DATA_MENU: MenuItem[] = []

// =============================================
// 5. 历史知识与语义菜单（兼容导出）
// =============================================

export const KNOWLEDGE_MENU: MenuItem[] = [
  {
    key: 'knowledge-semantic',
    title: '知识与语义',
    icon: markRaw(IconBook),
    children: [
      { key: 'governance-metrics', title: '指标', route: '/knowledge/metrics' },
      { key: 'governance-business-terms', title: '业务术语', route: '/knowledge/business-terms' },
      { key: 'governance-subject-tree', title: '主题树', route: '/knowledge/subject-tree' },
      { key: 'governance-report-templates', title: '报表模板', route: '/knowledge/report-templates' },
      { key: 'governance-dimensions', title: '维度', route: '/knowledge/dimensions' },
    ]
  },
  {
    key: 'knowledge-sql',
    title: 'SQL 知识',
    children: [
      { key: 'governance-sql-examples', title: 'SQL 样例', route: '/knowledge/sql-examples' },
      { key: 'governance-instructions', title: '查询指令', route: '/knowledge/instructions' },
    ]
  },
  {
    key: 'knowledge-base-group',
    title: '知识库',
    children: [
      { key: 'governance-knowledge-base', title: '知识库', route: '/knowledge/knowledge-base' },
    ]
  },
]

// =============================================
// 6. 历史智能体工程菜单（兼容导出）
// =============================================

export const AGENT_MENU: MenuItem[] = [
  {
    key: 'memory-os',
    title: '记忆治理',
    children: [
      { key: 'memory-openviking', title: 'OpenViking 记忆管理', route: '/control/memory/openviking' },
    ]
  },
]

// =============================================
// 7. 历史自动化运营菜单（兼容导出）
// =============================================

export const OPERATIONS_MENU: MenuItem[] = [
  {
    key: 'ops-tracking',
    title: '执行监控',
    icon: markRaw(IconThunderbolt),
    children: [
      { key: 'governance-traces', title: '执行概览', route: '/mission/tracing' },
      { key: 'mission-task-center', title: '任务中心', route: '/mission/tasks' },
      { key: 'governance-pending-tasks', title: '待处理任务', route: '/mission/pending-tasks' },
    ]
  },
  {
    key: 'ops-scheduling',
    title: '调度编排',
    children: [
      { key: 'governance-scheduler', title: '周期任务', route: '/mission/scheduler' },
      { key: 'workflow-list', title: '工作流编排', route: '/mission/workflows' },
      { key: 'workflow-tools', title: '工作流工具', route: '/mission/workflow-tools' },
    ]
  },
]

// =============================================
// 8. 历史运维菜单（兼容导出）
// =============================================

export const OPS_MENU: MenuItem[] = []

// =============================================
// 9. 历史管理菜单（兼容导出）
// =============================================

export const ADMIN_MENU: MenuItem[] = [
  {
    key: 'admin-project',
    title: '项目管理',
    icon: markRaw(IconSettings),
    children: [
      { key: 'governance-projects', title: '项目', route: '/control/projects' },
      { key: 'governance-model-cost', title: '模型成本', route: '/control/model-cost' },
    ]
  },
  {
    key: 'admin-permissions',
    title: '权限与用户',
    icon: markRaw(IconSafe),
    children: [
      { key: 'governance-permissions-home', title: '权限概览', route: '/control/permissions' },
      { key: 'governance-permissions-catalog', title: '权限项', route: '/control/permissions/catalog' },
      { key: 'governance-permissions-roles', title: '角色', route: '/control/permissions/roles' },
      { key: 'governance-permissions-users', title: '用户授权', route: '/control/permissions/users' },
      { key: 'governance-permissions-agents', title: '智能体授权', route: '/control/permissions/agents' },
      { key: 'governance-user-management', title: '用户管理', route: '/control/user-management' },
    ]
  },
]

// =============================================
// 六域产品菜单
// =============================================

/**
 * 智能体工作台：对话、执行、工具和产物的主入口。
 * 现阶段只暴露已存在的统一工作台路由，快捷治理入口由工作台内部启动器承载。
 */
export const AGENT_WORKSPACE_MENU: MenuItem[] = [
  {
    key: 'agent-workspace-home',
    title: '智能体工作台',
    icon: markRaw(IconRobot),
    children: [
      { key: 'agent-workspace', title: '智能体工作台', route: '/agents/workspace' },
    ],
  },
]

/**
 * 业务工作台：多业务场景的一线执行台。只挂真实路由，未来业务场景开发完再补叶子入口。
 */
export const BUSINESS_WORKBENCH_MENU: MenuItem[] = [
  {
    key: 'business-tasks',
    title: '业务任务',
    icon: markRaw(IconDashboard),
    children: [
      { key: 'document-search', title: '文档检索', route: '/workbench/document-search' },
    ],
  },
  {
    key: 'business-growth',
    title: '增长运营',
    children: [
      { key: 'growth-strategy-list', title: '增长策略', route: '/mission/growth/strategies' },
      { key: 'competitor-materials', title: '竞品素材', route: '/mission/growth/materials' },
    ],
  },
  {
    key: 'business-channel-management',
    title: '渠道管理',
    children: [
      { key: 'channel-management', title: '渠道管理', route: '/mission/channel-management' },
    ],
  },
]

export const DATA_PLATFORM_MENU: MenuItem[] = [
  {
    key: 'data-catalog',
    title: '数据目录',
    icon: markRaw(IconStorage),
    children: [
      { key: 'datalake-catalog', title: '数据目录', route: '/knowledge/datalake/unified-catalog' },
      { key: 'track-catalog', title: '埋点目录', route: '/knowledge/datalake/track-catalog' },
      { key: 'datalake-datasets', title: '数据集', route: '/knowledge/datalake/datasets' },
      { key: 'governance-tables-metadata', title: '业务元数据', route: '/knowledge/tables-metadata' },
    ],
  },
]

export const AGENT_ENGINEERING_MENU: MenuItem[] = [
  {
    key: 'agent-mgmt',
    title: '智能体管理',
    icon: markRaw(IconRobot),
    children: [
      { key: 'agent-list', title: '智能体目录', route: '/agent/agents' },
      { key: 'capability-config', title: '能力配置', route: '/agent/agents/capabilities' },
      { key: 'prompt-engineering', title: '提示词工程', route: '/agent/agents/prompt-engineering' },
      { key: 'agent-model-settings', title: '模型路由', route: '/agent/agents/model-settings' },
    ],
  },
  {
    key: 'knowledge-semantic',
    title: '知识与语义',
    icon: markRaw(IconBook),
    children: [
      { key: 'governance-knowledge-base', title: '知识库', route: '/knowledge/knowledge-base' },
      { key: 'governance-metrics', title: '指标', route: '/knowledge/metrics' },
      { key: 'governance-business-terms', title: '业务术语', route: '/knowledge/business-terms' },
      { key: 'governance-subject-tree', title: '主题树', route: '/knowledge/subject-tree' },
      { key: 'governance-dimensions', title: '维度', route: '/knowledge/dimensions' },
      { key: 'governance-report-templates', title: '报表模板', route: '/knowledge/report-templates' },
      { key: 'governance-sql-examples', title: 'SQL 样例', route: '/knowledge/sql-examples' },
      { key: 'governance-instructions', title: '查询指令', route: '/knowledge/instructions' },
    ],
  },
  {
    key: 'model-mgmt',
    title: '模型管理',
    icon: markRaw(IconApps),
    children: [
      { key: 'governance-model', title: '模型配置', route: '/agent/model' },
    ],
  },
  {
    key: 'memory-os',
    title: '记忆治理',
    children: [
      { key: 'memory-openviking', title: 'OpenViking 记忆管理', route: '/control/memory/openviking' },
    ],
  },
]

export const MANAGEMENT_OPERATIONS_MENU: MenuItem[] = [
  {
    key: 'admin-project',
    title: '项目与成本',
    icon: markRaw(IconSettings),
    children: [
      { key: 'governance-projects', title: '项目', route: '/control/projects' },
      { key: 'governance-model-cost', title: '模型成本', route: '/control/model-cost' },
    ],
  },
  ...ADMIN_MENU.filter((item) => item.key !== 'admin-project'),
  ...OPS_MENU,
]

/**
 * 六域菜单列表（用于顶部应用中心和产品域归属）
 */
export const PRODUCT_DOMAINS = [
  { key: 'agent-workspace', title: '智能体工作台', menu: AGENT_WORKSPACE_MENU },
  { key: 'workbench', title: '业务工作台', menu: BUSINESS_WORKBENCH_MENU },
  { key: 'data', title: '数据平台', menu: DATA_PLATFORM_MENU },
  { key: 'agent', title: '智能体工程', menu: AGENT_ENGINEERING_MENU },
  { key: 'operations', title: '自动化运营', menu: OPERATIONS_MENU },
  { key: 'admin', title: '管理与运维', menu: MANAGEMENT_OPERATIONS_MENU },
]

// =============================================
// 产品菜单映射（路由前缀 → 菜单树）
// Phase 1: 菜单域名称升级，路由前缀暂保留旧值
// =============================================

/**
 * 全域合并菜单（侧边栏根据当前 route 自动高亮）
 * Phase 1 策略：所有旧前缀指向同一棵合并树，避免路由不匹配
 */
const _ALL_MENU: MenuItem[] = [
  ...AGENT_WORKSPACE_MENU,
  ...BUSINESS_WORKBENCH_MENU,
  ...DATA_PLATFORM_MENU,
  ...AGENT_ENGINEERING_MENU,
  ...OPERATIONS_MENU,
  ...MANAGEMENT_OPERATIONS_MENU,
]

/**
 * ``route.meta.menuGroup`` → ``PRODUCT_DOMAINS.key`` 的映射表。
 *
 * router 里历史上用了多组 menuGroup 值，新六域需要保留别名映射，避免旧 URL
 * 和旧路由 meta 改名后丢失高亮、侧栏和权限归属。
 *
 * 新增路由约定：meta.menuGroup **首选用 6 个 domain key 之一**（直接命中），
 * 仅当历史遗留值时走此表兜底。
 */
export const MENU_GROUP_TO_DOMAIN: Record<string, string> = {
  // 六域同名（约定入口）
  'agent-workspace': 'agent-workspace',
  workbench: 'workbench',
  data: 'data',
  agent: 'agent',
  operations: 'operations',
  admin: 'admin',

  // 旧九域 → 六域
  asset: 'data',
  integration: 'data',
  governance: 'data',
  knowledge: 'agent',
  ops: 'admin',

  // 历史别名 → 六域
  'ai-datalake': 'data',
  'agent-mgmt': 'agent',
  'model-mgmt': 'agent',
  'ops-center': 'operations',
  'hitl-policy': 'operations',
  'platform-mgmt': 'admin',
}

/**
 * 根据路由查找所属域。
 *
 * 匹配策略（优先级由高到低）：
 * 1. ``meta.menuGroup`` 声明（最强意图信号）：
 *    - 直接命中 ``PRODUCT_DOMAINS.key`` → 立即返回
 *    - 命中 ``MENU_GROUP_TO_DOMAIN`` 映射 → 返回映射后的 domain
 * 2. 精确匹配：域内某个叶子节点的 ``route === path``
 * 3. 前缀匹配（段边界安全）：域内某个叶子节点的 ``route`` 是 ``path`` 的前缀段
 *    例如 ``/agent/agents/xxx/edit`` 命中叶子 ``/agent/agents``，归属智能体工程
 *    多个命中时取「最长前缀」胜出，保证详情/编辑等子路由归属到正确域
 *
 * @param path  当前路由 ``route.path``
 * @param menuGroup  当前路由 ``route.meta.menuGroup``（可选，但强烈推荐传）
 */
export function findDomainByRoute(
  path: string,
  menuGroup?: string | null,
): typeof PRODUCT_DOMAINS[number] | undefined {
  // ① meta.menuGroup 优先：设计意图最强信号
  if (menuGroup) {
    const domainKey = MENU_GROUP_TO_DOMAIN[menuGroup] ?? menuGroup
    const byMeta = PRODUCT_DOMAINS.find((d) => d.key === domainKey)
    if (byMeta) return byMeta
  }

  // ② 精确叶子匹配
  for (const domain of PRODUCT_DOMAINS) {
    const found = findMenuByRoute(domain.menu, path)
    if (found) return domain
  }

  // ③ 最长前缀段匹配（救详情/编辑等子路由）
  let bestDomain: typeof PRODUCT_DOMAINS[number] | undefined
  let bestLength = -1
  for (const domain of PRODUCT_DOMAINS) {
    for (const leaf of getLeafMenuItems(domain.menu)) {
      const leafRoute = leaf.route
      if (!leafRoute) continue
      const isSegmentPrefix = path === leafRoute || path.startsWith(leafRoute + '/')
      if (isSegmentPrefix && leafRoute.length > bestLength) {
        bestLength = leafRoute.length
        bestDomain = domain
      }
    }
  }
  return bestDomain
}

export const PRODUCT_MENU_MAP: Record<string, { title: string; menu: MenuItem[] }> = {
  '/knowledge': { title: '望舒平台', menu: _ALL_MENU },
  '/agent': { title: '望舒平台', menu: _ALL_MENU },
  '/agents': { title: '望舒平台', menu: _ALL_MENU },
  '/mission': { title: '望舒平台', menu: _ALL_MENU },
  '/control': { title: '望舒平台', menu: _ALL_MENU },
  '/workbench': { title: '望舒平台', menu: _ALL_MENU },
}

// =============================================
// 兼容导出（deprecated）
// =============================================

/**
 * @deprecated v7.0 后请使用 PRODUCT_DOMAINS 六域菜单
 * 仅供 Sidebar.vue / ThreeLevelMenu.vue 等旧组件消费
 */
export const GOVERNANCE_MENU: MenuItem[] = [
  ..._ALL_MENU,
]

/**
 * @deprecated v7.0 后请使用 PRODUCT_MENU_MAP
 */
export const DATAMANUS_MENU: MenuItem[] = [
  {
    key: 'governance',
    title: '平台菜单',
    icon: markRaw(IconSettings),
    children: GOVERNANCE_MENU,
  }
]

// =============================================
// 旧名称兼容（deprecated）
// =============================================

/** @deprecated 使用 OPERATIONS_MENU */
export const MISSION_MENU = OPERATIONS_MENU

/** @deprecated 使用 MANAGEMENT_OPERATIONS_MENU */
export const CONTROL_MENU = MANAGEMENT_OPERATIONS_MENU

// =============================================
// 全域洞察
// =============================================

/**
 * 全域洞察菜单（用于 SlimSidebar）
 */
export const INSIGHT_MENU: MenuItem[] = [
  { key: 'my-dashboards', title: '我的看板', children: [] },
  { key: 'project-space', title: '项目空间', children: [] },
]

// =============================================
// 工具函数
// =============================================

/**
 * 查找菜单项的父级路径
 */
export function findMenuParentKeys(
  menuTree: MenuItem[],
  targetKey: string,
  parentKeys: string[] = []
): string[] | null {
  for (const item of menuTree) {
    if (item.key === targetKey) {
      return parentKeys
    }
    if (item.children) {
      const result = findMenuParentKeys(item.children, targetKey, [...parentKeys, item.key])
      if (result) return result
    }
  }
  return null
}

/**
 * 根据路由查找菜单项
 */
export function findMenuByRoute(menuTree: MenuItem[], route: string): MenuItem | null {
  for (const item of menuTree) {
    if (item.route === route) {
      return item
    }
    if (item.children) {
      const result = findMenuByRoute(item.children, route)
      if (result) return result
    }
  }
  return null
}

/**
 * 获取所有包含路由的叶子节点
 */
export function getLeafMenuItems(menuTree: MenuItem[]): MenuItem[] {
  const leaves: MenuItem[] = []

  function traverse(items: MenuItem[]) {
    for (const item of items) {
      if (item.route && (!item.children || item.children.length === 0)) {
        leaves.push(item)
      }
      if (item.children) {
        traverse(item.children)
      }
    }
  }

  traverse(menuTree)
  return leaves
}
